import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { addTeams, updateTeams } from '../../actions/userActions/teamAction'
import Loader from '../subComponents/Loader'
import { Redirect, useLocation, useHistory } from 'react-router-dom';
import HrComponent from '../reuse_components/HrComponent';
import BodyLargeMedium from '../designSystem/BodyLargeMedium';
import { IconButton } from '@mui/material';
import Textarea from '../designSystem/TextArea';
import BodyExtraSmallMedium from '../designSystem/BodyExtraSmallMedium';
import TextField from '../designSystem/TextField';
import designationLevelIcon from "../../assets/images/designationLevelIcon.svg";
import closeModal from "../../assets/images/closeModal.svg";
import LoadingOverlay from "../loadingOverlay/LoadingOverlay";
import teamIcon from "../../assets/images/Icon=Team.svg";
import { validateFields } from '../../utils/Validations';
import { Check, X } from 'lucide-react';
import BodyMediumMedium from '../designSystem/BodyMediumMedium';
import CancelButton from '../subComponents/buttons/CustomCancelButton';
import PrimaryButton from '../subComponents/buttons/PrimaryButton';
import { t } from '../../locales/translate';
import MultiSelect from '../designSystem/MultiSelect';
import { getAllDoctors , getAssignedDoctors} from '../../actions/userActions/doctorAction'
import { getProducts } from '../../actions/userActions/productAction'

// Custom hook for doctor data with pagination
const useDoctorData = (editDataId) => {
  const dispatch = useDispatch();
  const searchTimeoutRef = useRef(null);
  const currentSearchRef = useRef('');
  const currentPageRef = useRef(1);

  const [options, setOptions] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const loadDoctors = useCallback(async (searchText, page, type) => {
    // type: 'initial' | 'search' | 'more'
    if (type === 'initial') setIsInitialLoading(true);
    else if (type === 'search') setIsSearching(true);
    else setIsLoadingMore(true);

    try {
      const data = await dispatch(getAllDoctors({
        pageNumber: page,
        pageSize: 10,
        textSearch: searchText,
      }));

      const result = data?.result || [];
      currentPageRef.current = page;
      setHasMore(result.length === 10);

      const newOptions = result.map(d => ({ value: d.id, label: d.name }));
      if (page === 1) {
        setOptions(newOptions);
      } else {
        setOptions(prev => [...prev, ...newOptions]);
      }
    } finally {
      if (type === 'initial') setIsInitialLoading(false);
      else if (type === 'search') setIsSearching(false);
      else setIsLoadingMore(false);
    }
  }, [dispatch]);

  const loadNextPage = useCallback(() => {
    if (!hasMore || isLoadingMore || isSearching || isInitialLoading) return;
    loadDoctors(currentSearchRef.current, currentPageRef.current + 1, 'more');
  }, [hasMore, isLoadingMore, isSearching, isInitialLoading, loadDoctors]);

  const handleSearch = useCallback((inputValue) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    currentSearchRef.current = inputValue;
    searchTimeoutRef.current = setTimeout(() => {
      loadDoctors(inputValue, 1, 'search');
    }, 300);
  }, [loadDoctors]);

  // Reset to default page 1 whenever dropdown is reopened after a search
  const handleMenuOpen = useCallback(() => {
    if (currentSearchRef.current !== '') {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      currentSearchRef.current = '';
      currentPageRef.current = 1;
      loadDoctors('', 1, 'initial');
    }
  }, [loadDoctors]);

  // Reload when team changes (editDataId) or on initial mount
  useEffect(() => {
    currentSearchRef.current = '';
    currentPageRef.current = 1;
    setOptions([]);
    setHasMore(true);
    loadDoctors('', 1, 'initial');
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  // editDataId is intentionally the only changing dep — reloads on team switch
  // eslint-disable-next-line
  }, [editDataId]);

  return {
    doctorOptions: options,
    isInitialLoading,
    isLoadingMore,
    isSearching,
    hasMore,
    loadNextPage,
    handleSearch,
    handleMenuOpen,
  };
};
function AddTeams({ onClose, editData, onSave }) {

  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const initialValues = { name: "", description: "", active: false, doctors: [], products: [] };
  const [formValues, setFormValues] = useState(initialValues);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingAssignedDoctors, setIsLoadingAssignedDoctors] = useState(!!editData?.id);
  // Preselected doctor objects for edit mode
  const [assignedDoctorOptions, setAssignedDoctorOptions] = useState([]);

  // Products from Redux
  const reduxProducts = useSelector(state => state.products_reducer.products);
  const isProductsLoading = useSelector(state => state.products_reducer.isProductsLoading);
  const productOptions = Array.isArray(reduxProducts?.result) 
    ? reduxProducts.result.map(p => ({ value: p.id, label: p.name })) 
    : [];

  // Fetch products fresh on every open — re-runs when editData.id changes
  useEffect(() => {
    if (editData?.id) {
      dispatch(getProducts({
        filter: { textSearch: '', teamId: editData.id, isUpdate: true },
        pageNumber: -1,
        pageSize: 0,
      }));
    } else {
      dispatch(getProducts({
        filter: { textSearch: '', teamId: null, isUpdate: false },
        pageNumber: 1,
        pageSize: 100,
      }));
    }
  // eslint-disable-next-line
  }, [editData?.id]);

  // Preselect products assigned to this team in edit mode
  useEffect(() => {
    if (editData?.id && Array.isArray(reduxProducts?.result) && reduxProducts.result.length > 0) {
      const preselected = reduxProducts.result
        .filter(p => p.team?.id == editData.id)
        .map(p => p.id);
      setFormValues(prev => ({ ...prev, products: preselected }));
    }
  // eslint-disable-next-line
  }, [reduxProducts]);
  
  // Use custom hook for doctor data management
  const { 
    doctorOptions, 
    isInitialLoading,
    isLoadingMore,
    isSearching,
    hasMore,
    loadNextPage,
    handleSearch,
    handleMenuOpen,
  } = useDoctorData(editData?.id);

  // visibleDoctorOptions:
  // - Edit mode: assigned (preselected, checked) at top + paginated unassigned below
  // - Add mode: all paginated doctors
  const visibleDoctorOptions = useMemo(() => {
    if (editData?.id) {
      const assignedIds = new Set(assignedDoctorOptions.map(o => o.value));
      const unselected = doctorOptions.filter(d => !assignedIds.has(d.value));
      return [...assignedDoctorOptions, ...unselected];
    }
    return doctorOptions;
  }, [doctorOptions, assignedDoctorOptions, editData?.id]);
  // Initialize form fields immediately for edit mode
  useEffect(() => {
    if (editData?.id) {
      setFormValues(prev => ({
        ...prev,
        name: editData.name || '',
        description: editData.description || '',
        active: editData.active === true
      }));
    }
  }, [editData?.id]);

// Load assigned doctors every time editData changes (re-fetches on every open)
  useEffect(() => {
    const teamId = editData?.id;  // capture as primitive to avoid stale closure
    if (teamId) {
      setAssignedDoctorOptions([]);
      setIsLoadingAssignedDoctors(true);
      const loadEditDoctors = async () => {
        setFormValues(prev => ({ ...prev, doctors: [] }));
        try {
          const { assignedDoctors } = await dispatch(getAssignedDoctors(teamId));
          // Only keep doctors who belong to this team
          // Use == (loose) to handle string/number mismatch between editData.id and API t.id
          const filtered = (assignedDoctors || []).filter(d =>
            Array.isArray(d.teams) && d.teams.some(t => t.id == teamId)
          );
          const assignedOptions = filtered.map(d => ({ value: d.id, label: d.name }));
          const filteredIds = filtered.map(d => d.id);
          setAssignedDoctorOptions(assignedOptions);
          setFormValues(prev => ({
            ...prev,
            doctors: filteredIds
          }));
        } catch (error) {
          console.error('Error loading assigned doctors:', error);
        } finally {
          setIsLoadingAssignedDoctors(false);
        }
      };
      loadEditDoctors();
    } else {
      setIsLoadingAssignedDoctors(false);
    }
  }, [editData?.id, dispatch]);

  const handleDoctorChange = useCallback((selectedValues) => {
    setFormValues(prev => ({ ...prev, doctors: selectedValues }));
    // Clear specific field error when user makes selection
    if (formErrors.doctors) {
      setFormErrors(prev => ({ ...prev, doctors: undefined }));
    }
  }, [formErrors.doctors]);
   const handleSave = async (e) => {
    e.preventDefault();
    const errors = validateFields(formValues, "team");
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;
    
    setIsSubmitting(true);
    try {
      const { doctors, ...formDataWithoutDoctors } = formValues;
      const { products, ...formDataWithoutProducts } = formDataWithoutDoctors;
      const payload = {
        ...formDataWithoutProducts,
        doctorIds: doctors || [],
        productIds: products || []
      };
      
      if (editData) {
        console.log('Edit mode payload:', editData.id);
        payload.id = editData.id;
        await dispatch(updateTeams(payload));
      } else {
        await dispatch(addTeams(payload));
      }
      
      await onSave(payload);
    } catch (error) {
      console.error("Error saving team:", error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    setFormValues(prev => ({ ...prev, [name]: fieldValue }));
    
    // Clear specific field error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // Show loading overlay only during form submission
  const showLoadingOverlay = isSubmitting;

  const permissions = JSON.parse(localStorage.getItem("permissionsList"));

  const handleGoBack = () => {
    history.goBack();
  };

  return (
    <>
      <div className="dynamicmodal-overlay">
        <div className="dynamicmodal-container overlay-wrapper">
            <LoadingOverlay isVisible={showLoadingOverlay} />
          <div className={showLoadingOverlay ? 'blur' : ''}>
          <div className="dynamicmodal-header mb-3">
            <div className="dynamicmodal-header-left">
              <div className="icon">
                <img src={teamIcon} alt="teamIcon " />
              </div>
              <div className="text">
                <h2 className="modalheading">
                  {editData ? "Edit Team" :`${t("addTeam")}`}
                </h2>
                <p className="modaldescription">
                  {t("addTeamTip")}
                </p>
              </div>
            </div>
            <div className="dynamicmodal-header-right">
              <IconButton onClick={onClose}>
                <img src={closeModal} alt="" />
              </IconButton>
            </div>

          </div>


          <form onSubmit={handleSave}>
            <div className="dynamicmodal-body">

              <TextField
                name="name"
                label={`${t("name")} *`}
                type='text'
                value={formValues.name}
                onChange={handleChange}
                placeholder={t("name")}
                error={formErrors.name}
              />

              <Textarea
                name="description"
                label={`${t("description")} *`}
                placeholder={t("description")}
                value={formValues.description}
                onChange={handleChange}
                error={formErrors.description}
              />
              <MultiSelect
                  label="Add Doctors"
                  placeholder={isLoadingAssignedDoctors || isInitialLoading ? "Loading Doctors..." : "Select Doctors"}
                  options={visibleDoctorOptions}
                  value={formValues.doctors || []}
                  onChange={handleDoctorChange}
                  isSearchable={true}
                  isLoading={isLoadingAssignedDoctors || isInitialLoading || isSearching}
                  onInputChange={handleSearch}
                  onMenuOpen={handleMenuOpen}
                  onScrollBottom={loadNextPage}
                  isLoadingMore={isLoadingMore}
                  disabled={isSubmitting || isLoadingAssignedDoctors }
                  error={formErrors.doctors}
                />
              {false && (
                <MultiSelect
                  label="Add Products"
                  placeholder={isProductsLoading ? "Loading Products..." : "Select Products"}
                  options={productOptions}
                  value={formValues.products || []}
                  onChange={(selectedValues) => setFormValues(prev => ({ ...prev, products: selectedValues }))}
                  isSearchable={true}
                  isLoading={isProductsLoading}
                  disabled={isSubmitting || isProductsLoading}
                />
              )}
              <div className=" mt-2 ml-0">
                <div
                  className="form-group"
                  style={{ display: "flex", alignItems: "flex-start" }}
                >
                  <input
                    name="active"
                    style={{ cursor: "pointer", height: "14px", width: "14px" }}
                    type="checkbox"
                    checked={formValues.active}
                    className="ml-1 mt-1"
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                  <label
                    style={{ paddingLeft: "5px" }}
                    className="form-control-label"
                  >
                    <div style={{ padding: '2px' }}>
                      <BodyMediumMedium text={t("active")} color="#1E293B" />
                    </div>
                  </label>
                </div>
              </div>

            </div>
           <div className="dynamicmodal-footer">
                        <CancelButton
                         onClick={onClose}
                         text={t("cancel")}
                        width={document.body.dir === "rtl" ? "119px" : ""}
                        disabled={isSubmitting}
                         />
                         <PrimaryButton
                         text={editData ? t("update") : t("save")}
                         icon={Check}
                         width={document.body.dir === "rtl" ? "123px" : "88px"}
                         onClick={handleSave}
                         disabled={isSubmitting}
                         loading={isSubmitting}
                         />
                       </div>
          </form>
        </div>
      </div>
      </div>

    </>
  );
}

export default AddTeams
